/**
 * useCreditNotes Hook
 * React Query hooks for credit note operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCreditNotes,
  getCreditNoteById,
  createCreditNote,
  finalizeCreditNote,
  validateCreditNote,
  cancelCreditNote,
  peekNextInvoiceNumber
} from '../api/facturation.api';
import { invoiceKeys } from './useInvoices';
import type { CreditNoteFilters, CreateCreditNoteData } from '../types/facturation.types';

// Retry configuration for mutations with exponential backoff
// Only retry on network errors or 5xx server errors, not on 4xx client errors
const MUTATION_RETRY_CONFIG = {
  retry: (failureCount: number, error: unknown) => {
    // Don't retry on client errors (4xx) - these are validation errors
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: number }).status;
      if (status >= 400 && status < 500) return false;
    }
    // Also check for error message patterns indicating client error
    if (error && typeof error === 'object' && 'message' in error) {
      const msg = (error as { message: string }).message;
      if (msg.includes('ALREADY_CREDITED') || msg.includes('VALIDATION_ERROR')) return false;
    }
    return failureCount < 3;
  },
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // 1s, 2s, 4s... max 30s
};

// Query keys
export const creditNoteKeys = {
  all: ['creditNotes'] as const,
  lists: () => [...creditNoteKeys.all, 'list'] as const,
  list: (filters: CreditNoteFilters) => [...creditNoteKeys.lists(), filters] as const,
  details: () => [...creditNoteKeys.all, 'detail'] as const,
  detail: (id: number) => [...creditNoteKeys.details(), id] as const,
  nextNumber: (year?: number) => [...creditNoteKeys.all, 'next-number', year] as const,
};

// Get next credit note number
export const useNextCreditNoteNumber = (year?: number) => {
  return useQuery({
    queryKey: creditNoteKeys.nextNumber(year),
    queryFn: () => peekNextInvoiceNumber(year, 'CREDIT_NOTE'),
  });
};

// Get paginated credit notes
export const useCreditNotes = (filters: CreditNoteFilters = {}) => {
  return useQuery({
    queryKey: creditNoteKeys.list(filters),
    queryFn: () => getCreditNotes(filters),
    staleTime: 30 * 1000, // 30 seconds - shorter to ensure fresher data
    refetchOnWindowFocus: true,
  });
};

// Get single credit note by ID
export const useCreditNote = (id: number) => {
  return useQuery({
    queryKey: creditNoteKeys.detail(id),
    queryFn: () => getCreditNoteById(id),
    enabled: !!id,
  });
};

// Create credit note mutation
export const useCreateCreditNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCreditNoteData) => createCreditNote(data),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { invoiceIds }) => {
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.lists() });
      // Invalidate all linked invoices
      invoiceIds.forEach(invoiceId => {
        queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.sequence() });
      // Invalidate all eligibility queries - credit notes release products back for invoicing
      queryClient.invalidateQueries({ queryKey: [...invoiceKeys.all, 'eligibility'] });
    },
  });
};

// Finalize credit note mutation
// Accepts either just id (legacy) or object with id and sendEmail
export const useFinalizeCreditNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: number | { id: number; sendEmail?: boolean }) => {
      if (typeof params === 'number') {
        // Legacy: just call finalize without email
        await finalizeCreditNote(params);
        return null;
      }
      // Use validateCreditNote which supports sendEmail and returns CreditNote
      return validateCreditNote(params.id, params.sendEmail);
    },
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, params) => {
      const id = typeof params === 'number' ? params : params.id;
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.lists() });
    },
  });
};

// Cancel credit note mutation
export const useCancelCreditNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelCreditNote(id, reason),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.lists() });
      // Invalidate all eligibility queries - cancelling credit note affects available quantities
      queryClient.invalidateQueries({ queryKey: [...invoiceKeys.all, 'eligibility'] });
    },
  });
};
